/**
 * F1 - RECONCILING THE OM'S HEADLINE AGAINST ITS OWN RENT ROLL.
 *
 * ⭐⭐ THE DEFECT THIS EXISTS FOR. Every property the app builds from an OM carries TWO
 * independent statements of what it earns - the headline financial summary, and the rent roll -
 * and nothing compared them. Measured across the house-style six on 2026-08-27, two of five
 * disagreed, IN OPPOSITE DIRECTIONS, and both were wrong in the headline:
 *
 *   Guitar Center       stated $181,440   rent roll $172,440   the stated figure starts 2/1/2028
 *   Banc of California  stated $218,070   rent roll $224,612   the stated figure EXPIRED 8/1/2026
 *   Mojave River Acad   stated $215,454   rent roll $213,027   the $2,427 is REAL other income
 *
 * Guitar Center's own extracted escalation note reads "this is the NOI used for the marketed
 * 6.05% cap rate" - the extractor SAW it and the row stored the marketed figure anyway.
 *
 * ⭐ JOSEPH'S RULING, 2026-08-27: use the rent roll, and flag the difference. The rent roll is
 * the contractual document; the headline is marketing. ⛔ The flag is not decoration - the three
 * cases above are INDISTINGUISHABLE to the app, and only a human reading the OM can tell real
 * other income from a stale headline. The message therefore says what to DO, not merely that a
 * difference exists.
 */

export interface InPlaceIncomeInput {
  /** The contractual rent in force on `asOf`, from `annualRentRateAsOf`. */
  rentRollRate: number | null;
  /** What the OM's headline says - `property.gross_income`. */
  statedIncome: number | null;
  /** The day both are supposed to describe. */
  asOf: string;
}

export interface InPlaceIncomeResult {
  /** What to use as the income basis. `null` when neither source is usable. */
  basis: number | null;
  source: 'rent_roll' | 'stated' | 'none';
  /** `stated - rentRoll`. Positive means the headline is HIGHER. `null` when not comparable. */
  difference: number | null;
  /** Shown on screen when it matters, null when there is nothing to say. */
  message: string | null;
}

/** Sub-dollar gaps are rounding, not disagreement. */
const TOLERANCE = 1;

const money = (v: number): string =>
  '$' + Math.round(v).toLocaleString('en-US');

const usable = (v: unknown): v is number => typeof v === 'number' && isFinite(v);

export const reconcileInPlaceIncome = (input: InPlaceIncomeInput): InPlaceIncomeResult => {
  const { rentRollRate, statedIncome, asOf } = input;
  const hasRoll = usable(rentRollRate);
  const hasStated = usable(statedIncome);

  if (!hasRoll && !hasStated) return { basis: null, source: 'none', difference: null, message: null };

  if (!hasRoll) {
    // No rent roll at all - the overwhelming majority of properties. Behave exactly as before.
    return { basis: statedIncome as number, source: 'stated', difference: null, message: null };
  }

  // ⛔ A rent roll of zero means no lease is in term today - a vacant building, or a roll the
  // extractor could not read. Preferring it would silently zero out a real deal.
  if ((rentRollRate as number) <= 0) {
    return {
      basis: hasStated ? (statedIncome as number) : null,
      source: hasStated ? 'stated' : 'none',
      difference: null,
      message: `No tenant is in term on ${asOf}, so the rent roll shows no rent. The stated income is being used - check the rent roll before trusting any figure on this deal.`,
    };
  }

  if (!hasStated) {
    return { basis: rentRollRate as number, source: 'rent_roll', difference: null, message: null };
  }

  const difference = (statedIncome as number) - (rentRollRate as number);
  if (Math.abs(difference) < TOLERANCE) {
    return { basis: rentRollRate as number, source: 'rent_roll', difference: 0, message: null };
  }

  const higher = difference > 0;
  const message =
    `The rent roll says ${money(rentRollRate as number)} is contractually in force on ${asOf}, ` +
    `but the stated income is ${money(statedIncome as number)} - ` +
    `${money(Math.abs(difference))} ${higher ? 'higher' : 'lower'}. ` +
    `The rent roll is being used. ` +
    `If the difference is other income, enter it as Other Income; ` +
    `if the stated figure is a future step or an expired rent, the rent roll is already correct.`;

  return { basis: rentRollRate as number, source: 'rent_roll', difference, message };
};
